import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../api/client';
import { ArrowLeft, Briefcase, MapPin, DollarSign, Calendar, ExternalLink, Clock, TrendingUp, Users } from 'lucide-react';

interface Vacancy {
  _id: string;
  title: string;
  status: string;
  department: string;
  budget?: number;
  createdAt: string;
  closedAt?: string;
  description?: string;
}

const VacancyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [vacancy, setVacancy] = useState<Vacancy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchVacancy = async () => {
      try {
        const res = await api.get(`/vacancies/${id}`);
        setVacancy(res.data);
        setError(null);
      } catch (err) {
        setError('Вакансия не найдена.');
      } finally {
        setLoading(false);
      }
    };
    fetchVacancy();
  }, [id]);

  if (loading) return <div style={{ padding: 60, textAlign: 'center' }}>Загрузка...</div>;
  if (error) return <div style={{ padding: 60, textAlign: 'center', color: '#ff4d4f' }}>{error}</div>;
  if (!vacancy) return null;

  const daysOpen = Math.ceil((new Date().getTime() - new Date(vacancy.createdAt).getTime()) / (1000 * 60 * 60 * 24));

  return (
    <div style={{ padding: '0 20px 40px 20px', fontFamily: 'Inter, sans-serif' }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <button onClick={() => navigate('/app/recruitment-analytics/vacancies')} style={styles.backBtn}>
          <ArrowLeft size={16} /> Назад к вакансиям
        </button>
        <button onClick={() => navigate(`/app/hr/vacancy/${vacancy._id}`)} style={{ ...styles.backBtn, borderColor: '#10b981', color: '#10b981' }}>
          <ExternalLink size={16} /> Открыть в CRM
        </button>
      </div>

      {/* Шапка */}
      <div style={{ ...styles.glassCard, marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: '#1a1a2e', margin: '0 0 8px 0' }}>{vacancy.title}</h1>
        <div style={{ display: 'flex', gap: 16, color: '#64748b', fontSize: 14 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Briefcase size={16} /> {vacancy.status}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><MapPin size={16} /> {vacancy.department || 'Без отдела'}</span>
        </div>
      </div>

      {/* KPI Карточки */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(37,117,252,0.1)', color: '#2575fc' }}><Clock size={24} /></div>
          <div><div style={styles.kpiLabel}>Дней в работе</div><div style={styles.kpiValue}>{daysOpen}</div></div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(16,185,129,0.1)', color: '#10b981' }}><DollarSign size={24} /></div>
          <div><div style={styles.kpiLabel}>Бюджет</div><div style={styles.kpiValue}>{vacancy.budget ? `${vacancy.budget.toLocaleString()} ₽` : '—'}</div></div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}><Users size={24} /></div>
          <div><div style={styles.kpiLabel}>Кандидатов</div><div style={styles.kpiValue}>—</div></div>
        </div>
      </div>

      {/* Основной контент */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
        <div style={styles.glassCard}>
          <h3 style={styles.sectionTitle}>Описание вакансии</h3>
          <p style={{ color: '#4a4a68', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
            {vacancy.description || 'Описание пока не заполнено. Вы можете добавить его в CRM.'}
          </p>
        </div>

        <div style={styles.glassCard}>
          <h3 style={styles.sectionTitle}>Детали</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={styles.row}><Calendar size={18} color="#64748b" /> Создана: {new Date(vacancy.createdAt).toLocaleDateString('ru-RU')}</div>
            {vacancy.closedAt && <div style={styles.row}><Calendar size={18} color="#64748b" /> Закрыта: {new Date(vacancy.closedAt).toLocaleDateString('ru-RU')}</div>}
            <div style={styles.row}><TrendingUp size={18} color="#64748b" /> Статус: {vacancy.status}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  glassCard: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 24, padding: 24, border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 12px 40px rgba(31,38,135,0.06)' },
  kpiCard: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', borderRadius: 20, padding: 20, border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 8px 24px rgba(31,38,135,0.04)', display: 'flex', alignItems: 'center', gap: 16 },
  kpiIcon: { width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  kpiLabel: { fontSize: 13, color: '#64748b', fontWeight: 600, marginBottom: 4 },
  kpiValue: { fontSize: 22, fontWeight: 800, color: '#1a1a2e' },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: '#1a1a2e', margin: '0 0 16px 0' },
  backBtn: { background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(10px)', border: '1px solid #2575fc', color: '#2575fc', borderRadius: 30, padding: '8px 16px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 14 },
  row: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: '#4a4a68' },
};

export default VacancyDetail;
